/**
 * Shared Arcade Dashboard display helpers.
 */

/** Build game_id -> human-readable name from /api/games and gamesConfig rows. */
export function buildGameNameMap(games = []) {
    const map = {};
    (games || []).forEach((g) => {
        const name = g.name || g.title;
        const ids = [g._id, g.id, g.backendId, g.game_id].filter(Boolean);
        if (!ids.length) return;
        ids.forEach((id) => {
            if (name) map[id] = name;
            else if (!map[id]) map[id] = id;
        });
    });
    return map;
}

/**
 * Map a backend game_id (e.g. snake2d) to the Arcade play route id (e.g. game6).
 * Falls back to the raw id when no catalog match exists.
 */
export function gamePlayRouteId(gameId, games = []) {
    if (!gameId) return null;
    const match = (games || []).find((g) => {
        const ids = [g.id, g.backendId, g._id, g.game_id].filter(Boolean);
        return ids.includes(gameId);
    });
    return (match && (match.id || match.backendId)) || gameId;
}

/** Resolve a friendly game title; falls back to the raw id. */
export function gameDisplayName(gameId, gameNameMap = {}) {
    if (!gameId) return '—';
    return gameNameMap[gameId] || gameId;
}

/** Human-readable game name for a leaderboard row (`game_id` / `game`). */
export function resolveGameLabel(entry = {}, gameNameMap = {}) {
    return gameDisplayName(entry.game_id || entry.game, gameNameMap);
}

/**
 * Dashboard aggregate leaderboard columns. Per-game pages omit Game
 * (`showGameColumn: false`) because every row is already one game.
 */
export function leaderboardTableColumns(showGameColumn = false) {
    const cols = [
        { key: 'rank', label: '#' },
        { key: 'agent_id', label: 'Agent / Player' },
        { key: 'username', label: 'User' },
    ];
    if (showGameColumn) cols.push({ key: 'game', label: 'Game' });
    cols.push(
        { key: 'score', label: 'Score' },
        { key: 'source', label: 'Source' },
        { key: 'timestamp', label: 'Date' },
        { key: 'replay', label: 'Replay' },
    );
    return cols;
}

/**
 * Short username for UI: local-part before '@' when the value looks like an email,
 * otherwise the username as-is. ObjectId-looking hashes fall back to a short stub
 * only when no better name is available.
 */
export function shortUsername(value) {
    if (value === undefined || value === null || value === '') return '—';
    const s = String(value).trim();
    if (!s) return '—';
    const at = s.indexOf('@');
    if (at > 0) return s.slice(0, at);
    // Avoid showing full ObjectId hashes as "usernames"
    if (/^[0-9a-fA-F]{24}$/.test(s)) return `${s.slice(0, 8)}…`;
    return s;
}

/** Prefer username/email fields, then fall back to user_id. */
export function resolveUserLabel(entry = {}) {
    return shortUsername(entry.display_name || entry.username || entry.email || entry.user_id || entry.player_id);
}

/**
 * Normalize a timestamp to epoch milliseconds for `new Date(...)`. DB-recorded
 * rows use ISO strings (fine as-is), but in-memory SDK session timestamps
 * (e.g. orchestrator `created_at`, from Python `time.time()`) are epoch
 * *seconds* — passing those straight to `Date` reads them as milliseconds and
 * renders dates around 1970. Seconds-based Unix timestamps stay under 1e12
 * until the year 33658, so anything smaller is assumed to be seconds.
 */
export function toEpochMs(value) {
    if (value === undefined || value === null || value === '') return null;
    if (typeof value === 'number') {
        return value < 1e12 ? value * 1000 : value;
    }
    const num = Number(value);
    if (!Number.isNaN(num) && String(value).trim() !== '') {
        return num < 1e12 ? num * 1000 : num;
    }
    return value;
}

/**
 * Agent / submitted-run display label with username prefix, e.g. "sub0 / agent_7d939a8f".
 * Manual / human rows show the player name only.
 */
export function isHumanRunSource(source) {
    const s = String(source || '').toLowerCase();
    return s === 'human' || s === 'manual';
}

/** Leaderboard Source column: Human / Manual vs Agent. */
export function formatRunSource(source) {
    const s = String(source || '').toLowerCase();
    if (s === 'manual') return 'Manual';
    if (s === 'human') return 'Human';
    if (s === 'agent' || s === 'sdk_client') return 'Agent';
    if (!s) return '—';
    return String(source);
}

export function formatAgentLabel(entry = {}, agentId = null) {
    if (isHumanRunSource(entry.source)) {
        return resolveUserLabel(entry);
    }
    const agent = agentId || entry.agent_id || entry.agent || entry.name || entry.player || entry.player_id;
    const user = resolveUserLabel(entry);
    if (!agent) return user !== '—' ? user : '—';
    if (!user || user === '—') return String(agent);
    const agentStr = String(agent);
    // Avoid double-prefixing if the agent id already starts with the username
    if (agentStr === user || agentStr.startsWith(`${user}/`) || agentStr.startsWith(`${user} /`)) {
        return agentStr;
    }
    return `${user} / ${agentStr}`;
}
